import { useCallback, useMemo, useState } from 'react'
import { Yakureki } from '@/shared/types/yakurekiCore'

/**
 * Drawer の患者選択モード。
 *
 * discriminated union により「batchDismiss と multiSelect の同時 true」を型レベルで不可能にする。
 * 旧設計 (Drawer.tsx の isBatchDismissMode + useMultiPatientSelection の isMultiSelectMode が独立 boolean)
 * では両方 true となる中間状態が表現可能で、実際に「まとめて完了 → mic → まとめて録音」経路で発生し
 * 「まとめて録音」ボタンがグレーアウトのまま使用不能となる UI バグを生んでいた。
 */
export type DrawerSelectionMode =
  | { kind: 'idle' }
  | { kind: 'batchDismiss'; ids: ReadonlySet<string> }
  | { kind: 'multiSelect'; ids: ReadonlySet<string> }

/**
 * まとめて録音の対象として選択可能か (NSIPS 由来 + 未録音 + 処理前のみ)。
 */
export const isSelectableForGroup = (patient: Yakureki): boolean =>
  patient.is_create_from_nsips &&
  !patient.has_audio &&
  patient.status === 'pending'

const EMPTY_SET: ReadonlySet<string> = new Set()

export interface UseDrawerSelectionModeReturn {
  mode: DrawerSelectionMode
  isBatchDismiss: boolean
  isMultiSelect: boolean
  isInSelectionMode: boolean
  selectedIds: ReadonlySet<string>
  selectionCount: number
  isSelected: (patientId: string) => boolean

  enterBatchDismiss: () => void
  enterMultiSelect: () => void
  exit: () => void
  togglePatient: (patientId: string) => void

  getSelectedPatients: (patients: Yakureki[]) => Yakureki[]
}

export const useDrawerSelectionMode = (): UseDrawerSelectionModeReturn => {
  const [mode, setMode] = useState<DrawerSelectionMode>({ kind: 'idle' })

  const enterBatchDismiss = useCallback(() => {
    setMode({ kind: 'batchDismiss', ids: new Set() })
  }, [])

  const enterMultiSelect = useCallback(() => {
    setMode({ kind: 'multiSelect', ids: new Set() })
  }, [])

  const exit = useCallback(() => {
    setMode({ kind: 'idle' })
  }, [])

  const togglePatient = useCallback((patientId: string) => {
    setMode((prev) => {
      if (prev.kind === 'idle') return prev
      const next = new Set(prev.ids)
      if (next.has(patientId)) {
        next.delete(patientId)
      } else {
        next.add(patientId)
      }
      return { ...prev, ids: next }
    })
  }, [])

  const selectedIds = mode.kind === 'idle' ? EMPTY_SET : mode.ids
  const selectionCount = selectedIds.size

  const isSelected = useCallback(
    (patientId: string) => selectedIds.has(patientId),
    [selectedIds]
  )

  const getSelectedPatients = useCallback(
    (patients: Yakureki[]) => patients.filter((p) => selectedIds.has(p.id)),
    [selectedIds]
  )

  return useMemo(
    () => ({
      mode,
      isBatchDismiss: mode.kind === 'batchDismiss',
      isMultiSelect: mode.kind === 'multiSelect',
      isInSelectionMode: mode.kind !== 'idle',
      selectedIds,
      selectionCount,
      isSelected,
      enterBatchDismiss,
      enterMultiSelect,
      exit,
      togglePatient,
      getSelectedPatients,
    }),
    [
      mode,
      selectedIds,
      selectionCount,
      isSelected,
      enterBatchDismiss,
      enterMultiSelect,
      exit,
      togglePatient,
      getSelectedPatients,
    ]
  )
}
